import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircleIcon, AlertTriangleIcon } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { z } from "zod";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import Logo from "../../components/Logo";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { useReset2FAAcceptMutation } from "../../services/api";

function inferError(error: unknown): string {
  const e = error as { status?: number };
  if (e?.status === 400) return "reset-2fa-accept.error-invalid-token";
  if (e?.status === 401) return "reset-2fa-accept.error-invalid-password";
  if (e?.status === 410) return "reset-2fa-accept.error-expired-token";
  return "reset-2fa-accept.error-generic";
}

export default function Reset2FAAccept() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotificationsProvider();
  const [reset2FAAccept] = useReset2FAAcceptMutation();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !email) {
      setError(t("reset-2fa-accept.missing-params"));
    }
  }, [token, email, t]);

  const schema = z.object({
    email: z
      .string()
      .min(1, t("reset-2fa-accept.required-email"))
      .email(t("reset-2fa-accept.invalid-email")),
    password: z.string().min(1, t("reset-2fa-accept.required-password")),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: email ?? "", password: "" },
  });

  useEffect(() => {
    if (email) form.setValue("email", email);
  }, [email, form]);

  async function onSubmit(values: Values) {
    if (!token) {
      setError(t("reset-2fa-accept.missing-token"));
      return;
    }
    setError(null);
    try {
      await reset2FAAccept({
        token,
        email: values.email,
        password: values.password,
      }).unwrap();
      showNotification("success", t("reset-2fa-accept.success"));
      navigate("/login");
    } catch (err) {
      setError(t(inferError(err)));
    }
  }

  const submitDisabled = !token || form.formState.isSubmitting;

  return (
    <>
      <Helmet>
        <title>{t("reset-2fa-accept.title")} | Betterfield</title>
      </Helmet>
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        <Form {...form}>
          <form
            autoComplete="off"
            noValidate
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <div className="space-y-2">
              <h1 className="text-2xl font-bold tracking-tight">
                {t("reset-2fa-accept.title")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("reset-2fa-accept.subtitle")}
              </p>
            </div>

            <Alert variant="warning">
              <AlertTriangleIcon />
              <AlertDescription>
                {t("reset-2fa-accept.warning")}
              </AlertDescription>
            </Alert>

            {error && (
              <Alert variant="destructive">
                <AlertCircleIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("reset-2fa-accept.email-label")}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="username"
                      disabled={!!email}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("reset-2fa-accept.password-label")}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      autoComplete="current-password"
                      show={showPassword}
                      onToggleShow={() => setShowPassword((s) => !s)}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col items-center gap-3">
              <Button
                type="submit"
                variant="destructive"
                size="lg"
                className="w-full"
                disabled={submitDisabled}
              >
                {t("reset-2fa-accept.submit-button")}
              </Button>
              <Button asChild variant="link" size="sm">
                <RouterLink to="/reset-2fa">
                  {t("reset-2fa-accept.link-expired")}
                </RouterLink>
              </Button>
              <Button asChild variant="link" size="sm">
                <RouterLink to="/login">
                  {t("reset-2fa-accept.back-to-login")}
                </RouterLink>
              </Button>
            </div>
          </form>
        </Form>
      </main>
    </>
  );
}
